import { useState, useEffect } from "react";
import { Container, Typography, Box, Card, CardContent, Grid, LinearProgress } from "@mui/material";
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import "../styles/Statistics.css";

const Statistics = () => {
  const [tasks, setTasks] = useState([]);
  const [, setSessions] = useState([]);
  const [stats, setStats] = useState({
    completedPomodoros: 0,
    totalFocusTime: 0,
    completedTasks: 0,
    streak: 0,
    totalSessions: 0,
    avgPomodorsPerSession: 0,
  });
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    // Load tasks and sessions from localStorage
    const tasksData = JSON.parse(localStorage.getItem("tasks")) || [];
    const sessionsData = JSON.parse(localStorage.getItem("sessions")) || [];

    setTasks(tasksData);
    setSessions(sessionsData);

    // Calculate statistics from actual completed Timer sessions, not manually-tracked task counters
    const pomodoroSessions = sessionsData.filter((s) => s.type === "pomodoro");
    const completedPomodoros = pomodoroSessions.length;
    const completedTasks = tasksData.filter((task) => task.completed).length;
    const totalFocusTime = pomodoroSessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    const streak = calculateStreak(sessionsData);
    const totalSessions = sessionsData.length;
    const avgPomodorsPerSession = completedPomodoros > 0 ? (totalFocusTime / completedPomodoros).toFixed(1) : 0;

    setStats({
      completedPomodoros,
      totalFocusTime,
      completedTasks,
      streak,
      totalSessions,
      avgPomodorsPerSession,
    });

    // Prepare chart data (last 7 days)
    const chartData = generateChartData(sessionsData);
    setChartData(chartData);
  }, []);

  const calculateStreak = (sessions) => {
    if (sessions.length === 0) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let streak = 0;
    let currentDate = new Date(today);

    const sessionsByDate = {};
    sessions
      .filter((session) => session.type === "pomodoro")
      .forEach((session) => {
        const sessionDate = new Date(session.completedAt);
        sessionDate.setHours(0, 0, 0, 0);
        const dateKey = sessionDate.toISOString().split("T")[0];
        if (!sessionsByDate[dateKey]) {
          sessionsByDate[dateKey] = true;
        }
      });

    while (sessionsByDate[currentDate.toISOString().split("T")[0]]) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    }

    return streak;
  };

  const generateChartData = (sessions) => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split("T")[0];
      const dayPomodoros = sessions
        .filter((s) => s.type === "pomodoro" && s.completedAt.startsWith(dateKey))
        .length;

      last7Days.push({
        date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        pomodoros: dayPomodoros,
      });
    }
    return last7Days;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <Container maxWidth="md" className="statistics-container" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        align="center"
        sx={{
          fontWeight: 900,
          fontFamily: "'Montserrat', sans-serif",
          background: "linear-gradient(90deg, #4CAF50, #45a049)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textTransform: "uppercase",
          letterSpacing: "2px",
          mb: 4,
        }}
        gutterBottom
      >
        Statistics & Analytics
      </Typography>

      {/* Key Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: "rgba(63, 81, 181, 0.1)", border: "2px solid #3F51B5" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed Pomodoros
              </Typography>
              <Typography variant="h5" sx={{ color: "#3F51B5", fontWeight: "bold" }}>
                {stats.completedPomodoros}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: "rgba(76, 175, 80, 0.1)", border: "2px solid #4CAF50" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Focus Time
              </Typography>
              <Typography variant="h5" sx={{ color: "#4CAF50", fontWeight: "bold" }}>
                {formatTime(stats.totalFocusTime)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: "rgba(255, 152, 0, 0.1)", border: "2px solid #FF9800" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed Tasks
              </Typography>
              <Typography variant="h5" sx={{ color: "#FF9800", fontWeight: "bold" }}>
                {stats.completedTasks}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ backgroundColor: "rgba(244, 67, 54, 0.1)", border: "2px solid #F44336" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                🔥 Current Streak
              </Typography>
              <Typography variant="h5" sx={{ color: "#F44336", fontWeight: "bold" }}>
                {stats.streak}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card sx={{ backgroundColor: "rgba(156, 39, 176, 0.1)", border: "2px solid #9C27B0" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Sessions
              </Typography>
              <Typography variant="h5" sx={{ color: "#9C27B0", fontWeight: "bold" }}>
                {stats.totalSessions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card sx={{ backgroundColor: "rgba(0, 150, 136, 0.1)", border: "2px solid #009688" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Minutes/Pomodoro
              </Typography>
              <Typography variant="h5" sx={{ color: "#009688", fontWeight: "bold" }}>
                {stats.avgPomodorsPerSession}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Last 7 Days Activity
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="pomodoros" fill="#3F51B5" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Task Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Task Completion Rate
          </Typography>
          {tasks.length > 0 ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  {stats.completedTasks} of {tasks.length} tasks completed
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(stats.completedTasks / tasks.length) * 100}
                  sx={{ height: 8, borderRadius: "4px" }}
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Completion Rate: {((stats.completedTasks / tasks.length) * 100).toFixed(1)}%
              </Typography>
            </>
          ) : (
            <Typography color="textSecondary">No tasks yet</Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default Statistics;
